import React, { useState, useEffect } from 'react';
import './Blogs.css';

/* ── Icons ──────────────────────────────────────────────── */
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="blog-spin" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
  </svg>
);

const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

/* ── Skeleton card ──────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="blog-card blog-card--skeleton">
    <div className="blog-card__content">
      <div className="blog-skel blog-skel--tag" />
      <div className="blog-skel blog-skel--title" />
      <div className="blog-skel blog-skel--line" />
      <div className="blog-skel blog-skel--line blog-skel--short" />
      <div className="blog-skel blog-skel--btn-group" />
    </div>
  </div>
);

function Blogs() {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      // const res = await fetch('http://localhost:5000/api/insights/blog');
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/insights/blog`);

      const result = await res.json();
      if (result.success) setInsights(result.data);
    } catch (err) {
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (insight) => {
    setSelectedInsight(insight);
    setPdfLoading(true);
    setShowViewModal(true);
    
    try {
      // const res = await fetch(`http://localhost:5000/api/insights/${insight.type}/${insight.slug}`);
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/insights/${insight.type}/${insight.slug}`);

      const result = await res.json();
      if (result.success) {
        setSelectedInsight(result.data);
      }
    } catch (err) {
      console.error('Error fetching insight details:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadRequest = (insight) => {
    setSelectedInsight(insight);
    setShowDownloadModal(true);
    setError('');
    setSuccess(false);
    setFormData({ name: '', email: '' });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  // FIXED: Updated download handler to properly download PDF files
  const handleDownload = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    const emailRx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRx.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setDownloading(true);
    setError('');

    try {
      const response = await fetch(
        // `http://localhost:5000/api/insights/${selectedInsight.type}/${selectedInsight.slug}/download`,
        `${import.meta.env.VITE_API_URL}/api/insights/${selectedInsight.type}/${selectedInsight.slug}/download`,

        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Download failed');
      }

      // Get the file as blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedInsight.fileName || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccess(true);
      setTimeout(() => {
        setShowDownloadModal(false);
        setSuccess(false);
        fetchBlogs();
      }, 1800);
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const closeModals = () => {
    if (!downloading) {
      setShowViewModal(false);
      setShowDownloadModal(false);
      setError('');
      setSuccess(false);
      setSelectedInsight(null);
      setPdfLoading(false);
    }
  };

  return (
    <div className="blog-page">
      <div className="blog-hero">
        <div className="blog-hero__inner">
          <p className="blog-hero__eyebrow">BaiRees Global Advisory</p>
          <h1 className="blog-hero__title">Blogs & Articles</h1>
          <p className="blog-hero__sub">
            Insights, trends, and expert perspectives on technology,
            transformation, and the future of business.
          </p>
        </div>
      </div>

      <div className="blog-body">
        {loading ? (
          <div className="blog-grid">
            {[1, 2, 3, 4].map((n) => <SkeletonCard key={n} />)}
          </div>
        ) : insights.length === 0 ? (
          <div className="blog-empty">
            <p>No blog posts available at this time.</p>
          </div>
        ) : (
          <div className="blog-grid">
            {insights.map((insight) => (
              <div key={insight._id} className="blog-card">
                <div className="blog-card__content">
                  <div className="blog-card__meta">
                    <span className="blog-tag">Blog</span>
                    <span className="blog-date">{new Date(insight.publishedAt).toLocaleDateString()}</span>
                    <span className="blog-downloads">
                      <DownloadIcon />
                      {insight.downloadCount}
                    </span>
                  </div>
                  <h3 className="blog-card__title">{insight.title}</h3>
                  <p className="blog-card__excerpt">{insight.excerpt}</p>
                  {insight.tags && insight.tags.length > 0 && (
                    <div className="blog-card__tags">
                      {insight.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="blog-tag-sm">{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="blog-card__footer">
                    <button
                      className="blog-btn blog-btn--view"
                      onClick={() => handleView(insight)}
                    >
                      <ViewIcon />
                      Read Article
                    </button>
                    <button
                      className="blog-btn blog-btn--download"
                      onClick={() => handleDownloadRequest(insight)}
                    >
                      <DownloadIcon />
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Modal with Iframe PDF Viewer */}
      {showViewModal && selectedInsight && (
        <div className="blog-overlay" onClick={closeModals}>
          <div className="blog-modal blog-modal--pdf" onClick={(e) => e.stopPropagation()}>
            <div className="blog-modal__header">
              <div>
                <p className="blog-modal__eyebrow">Blog Article</p>
                <h2 className="blog-modal__title">{selectedInsight.title}</h2>
                <div className="blog-modal__meta">
                  <span>{new Date(selectedInsight.publishedAt).toLocaleDateString()}</span>
                </div>
              </div>
              <button className="blog-modal__close" onClick={closeModals}>
                <CloseIcon />
              </button>
            </div>
            <div className="blog-modal__body blog-modal__body--pdf">
              {pdfLoading ? (
                <div className="blog-pdf-loading">
                  <div className="blog-spinner"></div>
                  <p>Loading PDF viewer...</p>
                </div>
              ) : selectedInsight.fileUrl ? (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedInsight.fileUrl)}&embedded=true`}
                  title={selectedInsight.title}
                  className="blog-pdf-iframe"
                  frameBorder="0"
                  allowFullScreen
                />
              ) : (
                <div className="blog-pdf-error">
                  <p>PDF viewer not available. Please download the file instead.</p>
                  <button 
                    className="blog-btn blog-btn--download"
                    onClick={() => {
                      setShowViewModal(false);
                      handleDownloadRequest(selectedInsight);
                    }}
                  >
                    <DownloadIcon />
                    Download PDF
                  </button>
                </div>
              )}
            </div>
            <div className="blog-modal__footer">
              <div className="blog-view-stats">
                <span>👁️ {selectedInsight.viewCount} views</span>
                <span>📥 {selectedInsight.downloadCount} downloads</span>
              </div>
              <button 
                className="blog-btn blog-btn--download"
                onClick={() => {
                  setShowViewModal(false);
                  handleDownloadRequest(selectedInsight);
                }}
              >
                <DownloadIcon />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal - Requires name and email */}
      {showDownloadModal && (
        <div className="blog-overlay" onClick={closeModals}>
          <div className="blog-modal" onClick={(e) => e.stopPropagation()}>
            <div className="blog-modal__header">
              <div>
                <p className="blog-modal__eyebrow">Download Article</p>
                <h2 className="blog-modal__title">{selectedInsight?.title}</h2>
              </div>
              <button className="blog-modal__close" onClick={closeModals} disabled={downloading}>
                <CloseIcon />
              </button>
            </div>
            {success ? (
              <div className="blog-modal__success">
                <div className="blog-modal__success-icon">✓</div>
                <p>Your download has started!</p>
                <p className="blog-modal__success-sub">Thank you, {formData.name}!</p>
              </div>
            ) : (
              <div className="blog-modal__body">
                <p className="blog-modal__intro">
                  Please provide your details to download this article.
                </p>
                {error && <div className="blog-modal__error">{error}</div>}
                <form onSubmit={handleDownload}>
                  <div className="blog-field">
                    <label className="blog-field__label">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      disabled={downloading}
                      required
                    />
                  </div>
                  <div className="blog-field">
                    <label className="blog-field__label">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@company.com"
                      disabled={downloading}
                      required
                    />
                  </div>
                  <button type="submit" className="blog-btn blog-btn--submit" disabled={downloading}>
                    {downloading ? <><SpinnerIcon /> Processing…</> : <><DownloadIcon /> Download Now</>}
                  </button>
                </form>
                <p className="blog-modal__privacy">
                  Your details are used only to deliver this resource and will not be shared.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Blogs;